// UI-only state for the Tasks module (zustand 4). Server state lives in
// react-query; this store holds what the server must never know about:
// which drawer is open, the keyboard selection, palette/help visibility,
// collapsed sections and per-project view preferences (persisted).
import { create } from "zustand";
import { persist } from "zustand/middleware";

const persistedKeys = ["collapsed", "viewPrefs", "myGroup", "myView", "myScope", "lastProjectId"];

export const useUI = create(
  persist(
    (set, get) => ({
      // ── Drawer ──────────────────────────────────────────────────────
      drawerTaskId: null,
      drawerTab: 0,
      drawerFocus: null, // 'assignee' | 'due' | 'status' | 'priority' — set by A/D/S/P
      // TEMP 2026-09-07: default tab 0 = Overview (Evidence tab hidden). Was 1.
      openTask: (id, { tab = 0, focus = null } = {}) => set({ drawerTaskId: id, drawerTab: tab, drawerFocus: focus, selectedId: id }),
      closeDrawer: () => set({ drawerTaskId: null, drawerFocus: null }),
      setDrawerTab: (drawerTab) => set({ drawerTab }),
      clearDrawerFocus: () => set({ drawerFocus: null }),

      // ── Keyboard selection ──────────────────────────────────────────
      selectedId: null,
      visibleIds: [], // ordered ids of the list currently on screen
      setSelected: (selectedId) => set({ selectedId }),
      setVisibleIds: (visibleIds) => {
        const cur = get().visibleIds;
        if (cur.length === visibleIds.length && cur.every((x, i) => x === visibleIds[i])) return;
        set({ visibleIds });
      },
      moveSelection: (dir) => {
        const { visibleIds, selectedId } = get();
        if (!visibleIds.length) return;
        const i = visibleIds.indexOf(selectedId);
        const next = i < 0 ? (dir > 0 ? 0 : visibleIds.length - 1) : Math.max(0, Math.min(visibleIds.length - 1, i + dir));
        set({ selectedId: visibleIds[next] });
      },

      // ── Overlays ────────────────────────────────────────────────────
      paletteOpen: false,
      setPalette: (paletteOpen) => set({ paletteOpen }),
      helpOpen: false,
      setHelp: (helpOpen) => set({ helpOpen }),
      coachOpen: false,
      startCoach: () => set({ coachOpen: true, paletteOpen: false, helpOpen: false }),
      endCoach: () => set({ coachOpen: false }),
      newTaskBase: null, // null = closed; {} or {projectId, mode,...} = open with defaults
      openNewTask: (base = {}) => set((s) => ({ newTaskBase: s.newTaskBase ? { ...s.newTaskBase, ...base } : { ...base } })),
      closeNewTask: () => set({ newTaskBase: null }),
      dumpBase: null,
      openDump: (base = {}) => set((s) => ({ newTaskBase: { ...(s.newTaskBase || {}), ...base, mode: "command" }, dumpBase: null })),
      closeDump: () => set({ dumpBase: null, newTaskBase: get().newTaskBase?.mode === "command" ? null : get().newTaskBase }),
      wizard: null, // null | { tgProjectId?, templateId? }
      openWizard: (w = {}) => set({ wizard: w }),
      closeWizard: () => set({ wizard: null }),
      importOpen: false,
      setImport: (importOpen) => set({ importOpen }),
      mailPreview: null, // { subject, html, kind }
      setMailPreview: (mailPreview) => set({ mailPreview }),
      searchFocusTick: 0,
      focusSearch: () => set((s) => ({ searchFocusTick: s.searchFocusTick + 1 })),

      // ── Network status ──────────────────────────────────────────────
      rateLimitUntil: 0,
      setRateLimit: (rateLimitUntil) => set({ rateLimitUntil }),
      sse: "idle", // idle | live | reconnecting
      setSse: (sse) => set({ sse }),
      viewHotkey: 0, // 1–4 pressed; ProjectView mirrors it into the URL

      // ── Persisted prefs ─────────────────────────────────────────────
      collapsed: {},
      toggleSection: (key) => set((s) => ({ collapsed: { ...s.collapsed, [key]: !s.collapsed[key] } })),
      viewPrefs: {},
      setViewPref: (projectId, patch) => set((s) => ({ viewPrefs: { ...s.viewPrefs, [projectId]: { ...(s.viewPrefs[projectId] || {}), ...patch } } })),
      myGroup: "due",
      setMyGroup: (myGroup) => set({ myGroup }),
      myView: "list", // My tasks layout: "list" | "board" (personal kanban)
      setMyView: (myView) => set({ myView }),
      myScope: "everyone", // admin My Tasks: "mine" | "everyone" (members never send this)
      setMyScope: (myScope) => set({ myScope }),
      lastProjectId: null,
      setLastProjectId: (lastProjectId) => set({ lastProjectId }),
    }),
    {
      name: "tg.tasks.ui.v1",
      partialize: (s) => Object.fromEntries(persistedKeys.map((k) => [k, s[k]])),
    }
  )
);
